import React, { Component } from 'react';
import { withRouter } from 'utilities';
import { useAuth } from 'auth_context';
import { Loading } from 'components';

const FIELDS = [
	{ key: 'name', label: 'Name', placeholder: 'Full name' },
	{ key: 'username', label: 'Username', placeholder: 'username' },
	{ key: 'email', label: 'Email', placeholder: 'email@example.com' },
	{ key: 'phone', label: 'Phone', placeholder: '+1 555 000 0000' },
];

class AdminUsersPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			users: [],
			deleting: null,

			// expand / edit
			expandedId: null,
			expandedData: null,
			expandedLoading: false,
			expandedSaving: false,
			expandedSaved: false,
			expandedError: null,
			closingId: null,
			closingData: null,

			// create modal
			createOpen: false,
			createForm: { name: '', username: '', email: '', phone: '', password: '', is_staff: false, is_onboarded: false },
			createSaving: false,
			createError: null,

			// password modal
			pwdUserId: null,
			pwdUserName: '',
			pwdValue: '',
			pwdSaving: false,
			pwdError: null,
		};
	}

	componentDidMount() {
		this.fetchUsers();
	}

	fetchUsers = async () => {
		this.setState({ loading: true });
		const res = await this.props.auth.authFetch('/api/admin/users/');
		const data = await res.json();
		this.setState({ users: data.users || [], loading: false });
	};

	// ── Expand / collapse ──────────────────────────────────────────────────────

	toggleExpand = async userId => {
		const { expandedId, expandedData } = this.state;

		if (expandedId === userId) {
			this.setState({ expandedId: null, closingId: userId, closingData: expandedData });
			setTimeout(() => this.setState({ closingId: null, closingData: null, expandedData: null }), 450);
			return;
		}

		this.setState({
			expandedId: userId,
			expandedData: null,
			expandedLoading: true,
			expandedSaved: false,
			expandedError: null,
			closingId: expandedId,
			closingData: expandedData,
		});
		setTimeout(() => this.setState({ closingId: null, closingData: null }), 450);

		const user = this.state.users.find(u => u.id === userId);
		// data is already in the list — no extra fetch needed
		this.setState({
			expandedData: { ...user },
			expandedLoading: false,
		});
	};

	updateField = (key, value) => {
		this.setState(prev => ({ expandedData: { ...prev.expandedData, [key]: value } }));
	};

	saveExpanded = async () => {
		const { expandedId, expandedData } = this.state;
		this.setState({ expandedSaving: true, expandedError: null });

		try {
			const res = await this.props.auth.authFetch(`/api/admin/users/${expandedId}/`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(expandedData),
			});
			const data = await res.json();

			if (data.success) {
				this.setState(prev => ({
					users: prev.users.map(u => (u.id === expandedId ? data.user : u)),
					expandedData: data.user,
					expandedSaving: false,
					expandedSaved: true,
				}), () => setTimeout(() => this.setState({ expandedSaved: false }), 3000));
			} else {
				this.setState({ expandedSaving: false, expandedError: data.error || 'Save failed.' });
			}
		} catch {
			this.setState({ expandedSaving: false, expandedError: 'An error occurred.' });
		}
	};

	// ── Delete ─────────────────────────────────────────────────────────────────

	handleDelete = async (userId, userName) => {
		if (!window.confirm(`Delete "${userName || userId}"? This cannot be undone.`)) return;
		this.setState({ deleting: userId });

		const res = await this.props.auth.authFetch(`/api/admin/users/${userId}/`, { method: 'DELETE' });
		const data = await res.json();

		if (data.success) {
			this.setState(prev => ({
				users: prev.users.filter(u => u.id !== userId),
				deleting: null,
				expandedId: prev.expandedId === userId ? null : prev.expandedId,
			}));
		} else {
			alert(data.error || 'Delete failed.');
			this.setState({ deleting: null });
		}
	};

	// ── Create modal ───────────────────────────────────────────────────────────

	openCreate = () => this.setState({
		createOpen: true,
		createForm: { name: '', username: '', email: '', phone: '', password: '', is_staff: false },
		createError: null,
	});

	closeCreate = () => this.setState({ createOpen: false });

	updateCreateForm = (key, value) =>
		this.setState(prev => ({ createForm: { ...prev.createForm, [key]: value } }));

	createUser = async () => {
		const { createForm } = this.state;
		if (!createForm.username.trim() || !createForm.email.trim() || !createForm.password.trim()) return;

		this.setState({ createSaving: true, createError: null });
		try {
			const res = await this.props.auth.authFetch('/api/admin/users/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(createForm),
			});
			const data = await res.json();

			if (data.success) {
				this.setState({ createSaving: false, createOpen: false });
				await this.fetchUsers();
			} else {
				this.setState({ createSaving: false, createError: data.error || 'Failed to create user.' });
			}
		} catch {
			this.setState({ createSaving: false, createError: 'An error occurred.' });
		}
	};

	// ── Password modal ─────────────────────────────────────────────────────────

	openPwd = (userId, userName) =>
		this.setState({ pwdUserId: userId, pwdUserName: userName, pwdValue: '', pwdError: null, pwdSaving: false });

	closePwd = () => this.setState({ pwdUserId: null });

	setPassword = async () => {
		const { pwdUserId, pwdValue } = this.state;
		if (pwdValue.length < 8) {
			this.setState({ pwdError: 'Password must be at least 8 characters.' });
			return;
		}
		this.setState({ pwdSaving: true, pwdError: null });
		try {
			const res = await this.props.auth.authFetch(`/api/admin/users/${pwdUserId}/set_password/`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ password: pwdValue }),
			});
			const data = await res.json();
			if (data.success) {
				this.setState({ pwdUserId: null, pwdSaving: false });
			} else {
				this.setState({ pwdSaving: false, pwdError: data.error || 'Failed.' });
			}
		} catch {
			this.setState({ pwdSaving: false, pwdError: 'An error occurred.' });
		}
	};

	// ── Render ─────────────────────────────────────────────────────────────────

	render() {
		const {
			loading, users, deleting,
			expandedId, expandedData, expandedLoading, expandedSaving, expandedSaved, expandedError,
			closingId, closingData,
			createOpen, createForm, createSaving, createError,
			pwdUserId, pwdUserName, pwdValue, pwdSaving, pwdError,
		} = this.state;

		if (loading) return <Loading loading />;

		return (
			<div className="org-info-container">
				<div style={{ display: 'flex', alignItems: 'baseline', gap: '18px' }}>
					<h1 style={{ margin: 0 }}>All Users</h1>
					<span style={{ fontStyle: 'italic', color: 'rgba(220,220,220,0.7)', fontSize: '20px', position: 'relative', bottom: '2px' }}>
						[Admin]
					</span>
				</div>

				{/* ── Create modal ── */}
				{createOpen && (
					<div className="modal-overlay" onClick={this.closeCreate}>
						<div className="admin-create-modal" onClick={e => e.stopPropagation()}>
							<h2 style={{ margin: '0 0 24px' }}>Add User</h2>
							<div className="admin-create-fields">
								{[
									{ key: 'name', label: 'Name', placeholder: 'Full name' },
									{ key: 'username', label: 'Username', placeholder: 'username', required: true },
									{ key: 'email', label: 'Email', placeholder: 'email@example.com', required: true },
									{ key: 'phone', label: 'Phone', placeholder: '+1 555 000 0000' },
									{ key: 'password', label: 'Password', placeholder: 'Min 8 characters', required: true, type: 'password' },
								].map(({ key, label, placeholder, required, type }) => (
									<div key={key} className="admin-create-field">
										<label className="admin-create-label">
											{label}{required && <span className="admin-create-required"> *</span>}
										</label>
										<input
											className="admin-create-input"
											type={type || 'text'}
											value={createForm[key]}
											placeholder={placeholder}
											onChange={e => this.updateCreateForm(key, e.target.value)}
										/>
									</div>
								))}
								<label className="admin-create-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
									<input
										type="checkbox"
										className="org-detail-public-checkbox"
										checked={createForm.is_staff}
										onChange={e => this.updateCreateForm('is_staff', e.target.checked)}
									/>
									<span className="admin-create-label" style={{ margin: 0 }}>Admin (staff)</span>
								</label>
								<label className="admin-create-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
									<input
										type="checkbox"
										className="org-detail-public-checkbox"
										checked={createForm.is_onboarded}
										onChange={e => this.updateCreateForm('is_onboarded', e.target.checked)}
									/>
									<span className="admin-create-label" style={{ margin: 0 }}>Onboarded?</span>
								</label>
							</div>
							{createError && <p style={{ color: 'rgba(220,80,80,0.9)', fontSize: '0.85rem', marginBottom: '12px' }}>{createError}</p>}
							<div className="admin-create-footer">
								<button className="admin-org-btn" onClick={this.closeCreate}>Cancel</button>
								<button
									className="admin-org-btn admin-org-btn--save"
									onClick={this.createUser}
									disabled={createSaving || !createForm.username.trim() || !createForm.email.trim() || !createForm.password.trim()}
								>
									{createSaving ? 'Creating…' : 'Create User'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* ── Password modal ── */}
				{pwdUserId && (
					<div className="modal-overlay" onClick={this.closePwd}>
						<div className="admin-create-modal" onClick={e => e.stopPropagation()}>
							<h2 style={{ margin: '0 0 8px' }}>Change Password</h2>
							<p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: '24px' }}>{pwdUserName}</p>
							<div className="admin-create-fields">
								<div className="admin-create-field">
									<label className="admin-create-label">New Password <span className="admin-create-required">*</span></label>
									<input
										className="admin-create-input"
										type="password"
										value={pwdValue}
										placeholder="Min 8 characters"
										onChange={e => this.setState({ pwdValue: e.target.value, pwdError: null })}
									/>
								</div>
							</div>
							{pwdError && <p style={{ color: 'rgba(220,80,80,0.9)', fontSize: '0.85rem', marginBottom: '12px' }}>{pwdError}</p>}
							<div className="admin-create-footer">
								<button className="admin-org-btn" onClick={this.closePwd}>Cancel</button>
								<button
									className="admin-org-btn admin-org-btn--save"
									onClick={this.setPassword}
									disabled={pwdSaving || pwdValue.length < 8}
								>
									{pwdSaving ? 'Saving…' : 'Set Password'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* ── User list ── */}
				<div className="admin-org-list">
					{users.length === 0 && <p style={{ color: 'rgba(255,255,255,0.5)' }}>No users found.</p>}

					{users.map(user => {
						const isExpanded = expandedId === user.id;
						const isClosing = closingId === user.id;
						const cardData = isExpanded ? expandedData : (isClosing ? closingData : null);
						const isSelf = user.id === String(this.props.auth.user?.id);

						return (
							<div key={user.id} className={`admin-org-card ${isExpanded ? 'expanded' : ''}`}>
								<div className="admin-org-row">
									<div className="admin-org-info">
										<span className="admin-org-name">
											{user.name || user.username}
											{user.is_staff && (
												<span style={{ marginLeft: '8px', fontSize: '0.7rem', color: 'rgba(130,200,255,0.8)', fontWeight: 400 }}>
													admin
												</span>
											)}
											{!user.is_active && (
												<span style={{ marginLeft: '8px', fontSize: '0.7rem', color: 'rgba(220,100,100,0.8)', fontWeight: 400 }}>
													inactive
												</span>
											)}
										</span>
										<span className="admin-org-meta">
											@{user.username} · {user.email}
											{user.orgs.length > 0 && ` · ${user.orgs.map(o => o.name).join(', ')}`}
										</span>
									</div>
									<div className="admin-org-actions">
										<button
											className={`admin-org-btn ${isExpanded ? 'admin-org-btn--active' : 'admin-org-btn--edit'}`}
											onClick={() => this.toggleExpand(user.id)}
										>
											{isExpanded ? 'Close' : 'Edit'}
										</button>
										<button
											className="admin-org-btn admin-org-btn--edit"
											onClick={() => this.openPwd(user.id, user.name || user.username)}
										>
											Password
										</button>
										<button
											className="admin-org-btn admin-org-btn--delete"
											onClick={() => this.handleDelete(user.id, user.name || user.username)}
											disabled={deleting === user.id || isSelf}
											title={isSelf ? 'Cannot delete yourself' : ''}
										>
											{deleting === user.id ? '…' : 'Delete'}
										</button>
									</div>
								</div>

								<div className="admin-org-expand">
									<div className="admin-org-expand-inner">
										<div className="admin-org-expand-content">
											{expandedLoading && isExpanded ? (
												<p className="admin-org-expand-loading">Loading…</p>
											) : cardData ? (
												<>
													<div className="admin-org-fields">
														{FIELDS.map(({ key, label, placeholder }) => (
															<div key={key} className="admin-org-field-row">
																<span className="admin-org-field-label">{label}</span>
																<div className="admin-org-field-value">
																	<input
																		className="admin-org-field-input"
																		value={cardData[key] || ''}
																		placeholder={placeholder}
																		onChange={e => this.updateField(key, e.target.value)}
																	/>
																</div>
															</div>
														))}
														<div className="admin-org-field-row">
															<span className="admin-org-field-label">Admin</span>
															<div className="admin-org-field-value" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
																<input
																	type="checkbox"
																	className="org-detail-public-checkbox"
																	checked={cardData.is_staff || false}
																	onChange={e => this.updateField('is_staff', e.target.checked)}
																	disabled={isSelf}
																/>
																<span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem' }}>
																	{isSelf ? '(cannot change your own)' : 'Staff / admin access'}
																</span>
															</div>
														</div>
														<div className="admin-org-field-row">
															<span className="admin-org-field-label">Active</span>
															<div className="admin-org-field-value" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
																<input
																	type="checkbox"
																	className="org-detail-public-checkbox"
																	checked={cardData.is_active !== false}
																	onChange={e => this.updateField('is_active', e.target.checked)}
																	disabled={isSelf}
																/>
																<span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem' }}>
																	{isSelf ? '(cannot deactivate yourself)' : 'Can log in'}
																</span>
															</div>
														</div>
														<div className="admin-org-field-row">
															<span className="admin-org-field-label">Onboarded</span>
															<div className="admin-org-field-value" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
																<input
																	type="checkbox"
																	className="org-detail-public-checkbox"
																	checked={cardData.is_onboarded || false}
																	onChange={e => this.updateField('is_onboarded', e.target.checked)}
																/>
																<span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem' }}>
																	Has completed onboarding
																</span>
															</div>
														</div>
													</div>
													<div className="admin-org-expand-footer">
														{expandedError && isExpanded && (
															<span style={{ color: 'rgba(220,80,80,0.9)', fontSize: '0.85rem' }}>{expandedError}</span>
														)}
														{expandedSaved && isExpanded && (
															<span className="admin-org-saved-msg">Saved ✓</span>
														)}
														<button
															className="admin-org-btn admin-org-btn--save"
															onClick={this.saveExpanded}
															disabled={expandedSaving || !isExpanded}
														>
															{expandedSaving ? 'Saving…' : 'Save Changes'}
														</button>
													</div>
												</>
											) : null}
										</div>
									</div>
								</div>
							</div>
						);
					})}

					<button className="admin-org-add-btn" onClick={this.openCreate} title="Add user">+</button>
				</div>
			</div>
		);
	}
}

const AdminUsers = props => {
	const auth = useAuth();
	return <AdminUsersPage {...props} auth={auth} />;
};

export default withRouter(AdminUsers);
